import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";
import siteConfig from "../Datas/siteConfig";

export const CartContext = createContext(null);

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const clampQuantity = (value, maximum = 99) => {
  const quantity = Math.floor(Number(value) || 1);
  return Math.min(Math.max(quantity, 1), Math.max(maximum, 1));
};

const normaliseCart = (items) =>
  items.flatMap((item) => {
    const id = Number(item?.id);
    const product = productData.find((candidate) => candidate.id === id);
    if (!product) return [];

    return [
      {
        id,
        size: String(item.size || product.size?.[0] || "One size"),
        color: String(item.color || product.colors?.[0] || "Default"),
        quantity: clampQuantity(item.quantity, product.stock),
      },
    ];
  });

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normaliseCart(readStoredArray("cartItems"))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray("wishlistItems")
      .map(Number)
      .filter((id, index, values) =>
        productData.some((product) => product.id === id) &&
        values.indexOf(id) === index
      )
  );

  useEffect(() => {
    try {
      window.localStorage.setItem("cartItems", JSON.stringify(cartItems));
    } catch {
      // Storage may be unavailable in private browsing; the in-memory cart remains usable.
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "wishlistItems",
        JSON.stringify(wishlistItems)
      );
    } catch {
      // Keep wishlist interactions working even if persistence is unavailable.
    }
  }, [wishlistItems]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const id = Number(productId);
    const product = productData.find((candidate) => candidate.id === id);
    if (!product || product.stock < 1) return;

    const selectedSize = size || product.size?.[0] || "One size";
    const selectedColor = color || product.colors?.[0] || "Default";
    const requestedQuantity = clampQuantity(quantity, product.stock);

    setCartItems((items) => {
      const matchingItem = items.find(
        (item) =>
          item.id === id &&
          item.size === selectedSize &&
          item.color === selectedColor
      );

      if (!matchingItem) {
        return [
          ...items,
          {
            id,
            size: selectedSize,
            color: selectedColor,
            quantity: requestedQuantity,
          },
        ];
      }

      return items.map((item) =>
        item === matchingItem
          ? {
              ...item,
              quantity: clampQuantity(
                item.quantity + requestedQuantity,
                product.stock
              ),
            }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    const id = Number(productId);
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(
            item.id === id &&
            item.size === size &&
            item.color === color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const id = Number(productId);
    const product = productData.find((candidate) => candidate.id === id);
    if (!product) return;

    setCartItems((items) =>
      items.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity: clampQuantity(quantity, product.stock) }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    const id = Number(productId);
    if (!productData.some((product) => product.id === id)) return;
    setWishlistItems((items) => (items.includes(id) ? items : [...items, id]));
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = Number(productId);
    setWishlistItems((items) => items.filter((itemId) => itemId !== id));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = Number(productId);
    if (!productData.some((product) => product.id === id)) return;
    setWishlistItems((items) =>
      items.includes(id)
        ? items.filter((itemId) => itemId !== id)
        : [...items, id]
    );
  }, []);

  const totals = useMemo(() => {
    const cartCount = cartItems.reduce(
      (total, item) => total + item.quantity,
      0
    );
    const cartSubtotal = cartItems.reduce((total, item) => {
      const product = productData.find((candidate) => candidate.id === item.id);
      return total + (product?.price || 0) * item.quantity;
    }, 0);
    const shippingCost =
      cartSubtotal === 0 || cartSubtotal >= siteConfig.freeShippingThreshold
        ? 0
        : siteConfig.shippingCost;

    return {
      cartCount,
      cartSubtotal,
      shippingCost,
      orderTotal: cartSubtotal + shippingCost,
    };
  }, [cartItems]);

  const value = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlistItems.includes(Number(productId)),
      ...totals,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      totals,
      updateQuantity,
      wishlistItems,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
